'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@rr/ui'
import { useCompanyAddresses } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function AddressesList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyAddresses(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by city, street, label…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load addresses.'
          : 'No addresses in this organization.'
      }
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Default</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium text-foreground">
                  {a.label ?? '—'}
                </TableCell>
                <TableCell className="text-sm">{a.address ?? '—'}</TableCell>
                <TableCell className="text-sm">{a.city ?? '—'}</TableCell>
                <TableCell className="text-sm">
                  {a.country?.name ?? a.country?.code ?? '—'}
                </TableCell>
                <TableCell className="text-sm">
                  {a.isDefault ? 'Yes' : '—'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(a.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
